import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
  useSuspenseQuery
} from '@tanstack/react-query'
import { departureQueryOptions } from './data.ts'
import { DepartureList } from './components/departure-list.tsx'
import { MainContext } from './context/main.tsx'
import { FilterProvider, useFilters } from './context/filters.tsx'
import { FilterBar } from './components/filters.tsx'
import { applyFilterConfiguration } from './lib/filters.ts'

function AppContent ({ tourId, shipId, year }: { tourId?: string, shipId?: string, year?: number }): JSX.Element {
  const { data } = useSuspenseQuery(departureQueryOptions({ tourId, shipId, year }))
  const mainRef = useRef<HTMLElement | null>(null)
  const resizeObserver = useRef<ResizeObserver | null>(null)
  const { filters, sort } = useFilters()

  const filteredDepartures = useMemo(() => applyFilterConfiguration(data.departures, filters, sort), [data.departures, filters, sort])
  const departureListRef = useRef<HTMLDivElement>(null)
  const isInitialRender = useRef(true)

  useLayoutEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false
      return
    }
    departureListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [filters, sort])

  const mainRefCallback = useCallback((main: HTMLElement | null) => {
    if (resizeObserver.current == null) {
      resizeObserver.current = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.target instanceof HTMLElement) {
            entry.target.style.setProperty('--main-width', String(entry.contentBoxSize[0].inlineSize) + 'px')
          }
        }
      })
    }
    mainRef.current = main
    if (main != null) {
      resizeObserver.current.observe(main)
    } else {
      resizeObserver.current.disconnect()
    }
  }, [])

  return (
    <main ref={mainRefCallback}>
      <MainContext.Provider value={{ mainRef }}>
        <FilterBar departures={data.departures} translations={data.translations} />
        <DepartureList departures={filteredDepartures} translations={data.translations} ref={departureListRef} />
      </MainContext.Provider>
    </main>
  )
}

function App ({ tourId, shipId, year, dehydratedState }: {
  tourId: string | null
  shipId: string | null
  year: number | null
  dehydratedState?: DehydratedState | null
}): JSX.Element {
  const [queryClient] = useState(() => new QueryClient())

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        <FilterProvider>
          <AppContent tourId={tourId ?? undefined} shipId={shipId ?? undefined} year={year ?? undefined} />
        </FilterProvider>
      </HydrationBoundary>
    </QueryClientProvider>
  )
}

export default App
